import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { Check, Download } from "lucide-react";
import { confirmCheckout } from "@/lib/payments.functions";
import { getOnePagerContent } from "@/lib/onepager.functions";
import { buildOnePagerPdf } from "@/lib/onepager-pdf";
import { posthog } from "@/lib/posthog";


type SuccessSearch = {
  sub?: string;
  razorpay_payment_id?: string;
  razorpay_payment_link_id?: string;
  razorpay_payment_link_reference_id?: string;
  razorpay_payment_link_status?: string;
  razorpay_signature?: string;
};

export const Route = createFileRoute("/apply-success")({
  validateSearch: (search: Record<string, unknown>): SuccessSearch => ({
    sub: typeof search.sub === "string" ? search.sub : undefined,
    razorpay_payment_id: typeof search.razorpay_payment_id === "string" ? search.razorpay_payment_id : undefined,
    razorpay_payment_link_id: typeof search.razorpay_payment_link_id === "string" ? search.razorpay_payment_link_id : undefined,
    razorpay_payment_link_reference_id: typeof search.razorpay_payment_link_reference_id === "string" ? search.razorpay_payment_link_reference_id : undefined,
    razorpay_payment_link_status: typeof search.razorpay_payment_link_status === "string" ? search.razorpay_payment_link_status : undefined,
    razorpay_signature: typeof search.razorpay_signature === "string" ? search.razorpay_signature : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Application submitted - DeckScore" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SuccessPage,
});

function SuccessPage() {
  const { sub, razorpay_payment_id, razorpay_payment_link_id, razorpay_payment_link_reference_id, razorpay_payment_link_status, razorpay_signature } = Route.useSearch();
  const runConfirm = useServerFn(confirmCheckout);
  const runGetContent = useServerFn(getOnePagerContent);
  const ranRef = useRef(false);
  const [state, setState] = useState<"verifying" | "paid" | "unpaid" | "error">("verifying");
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfName, setPdfName] = useState<string>("one-pager.pdf");

  useEffect(() => {
    if (ranRef.current) return;
    if (!sub) {
      setState("error");
      return;
    }
    ranRef.current = true;
    runConfirm({ data: {
      submissionId: sub,
      paymentId: razorpay_payment_id,
      paymentLinkId: razorpay_payment_link_id,
      referenceId: razorpay_payment_link_reference_id,
      status: razorpay_payment_link_status,
      signature: razorpay_signature,
    } })
      .then(async (res) => {
        if (!res.paid) {
          setState("unpaid");
          return;
        }
        setState("paid");
        try { posthog.capture("payment_success", { submission_id: sub }); } catch { /* non-fatal */ }
        try {
          const cr = await runGetContent({ data: { submissionId: sub } });
          if (cr.ok) {
            const bytes = await buildOnePagerPdf(cr.content);
            const blob = new Blob([new Uint8Array(bytes)], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const base = (cr.content.headline || "one-pager").slice(0, 40);
            const slug =
              base
                .replace(/[^a-z0-9]+/gi, "-")
                .replace(/^-+|-+$/g, "")
                .toLowerCase() || "one-pager";
            setPdfName(`${slug}-one-pager.pdf`);
            setPdfUrl(url);
          }
        } catch (err) {
          console.error("one-pager pdf failed", err);

          // leave button in preparing state
        }
      })
      .catch(() => setState("error"));
  }, [sub, razorpay_payment_id, razorpay_payment_link_id, razorpay_payment_link_reference_id, razorpay_payment_link_status, razorpay_signature, runConfirm, runGetContent]);


  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  return (
    <div className="min-h-screen bg-bg text-ink flex flex-col">
      <header className="border-b border-line">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10 h-14 flex items-center">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-block h-[18px] w-[18px] rounded-md bg-accent" />
            <span className="font-display text-[15px] font-semibold">DeckScore</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 grid place-items-center px-6 py-16">
        <div className="w-full max-w-[520px] bg-surface border border-line rounded-2xl p-8 text-center">
          {state === "verifying" && (
            <>
              <p className="font-script text-accent text-[22px] leading-none">just a sec</p>
              <h1 className="font-display text-[24px] font-semibold mt-2">Confirming your payment…</h1>
              <p className="text-[14px] text-ink-secondary mt-2">
                Hang tight - this usually takes a couple of seconds.
              </p>
            </>
          )}

          {state === "paid" && (
            <>
              <div className="mx-auto h-14 w-14 rounded-full bg-accent/20 grid place-items-center mb-4">
                <Check className="h-7 w-7 text-accent" />
              </div>
              <p className="font-script text-accent text-[22px] leading-none">payment received</p>
              <h1 className="font-display text-[30px] font-semibold mt-1 mb-2">You're in.</h1>
              <p className="text-[14.5px] text-ink-secondary mb-6">
                That's everything we need from you. Our team now curates your one-pager and submits it to your thesis-matched programs. Any replies land straight in your inbox.
              </p>
              <div className="inline-flex items-center justify-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-4 py-2 text-[13px] text-ink mb-3">
                <Check className="h-3.5 w-3.5 text-accent" />
                <span>Application received - nothing more to do on your end</span>
              </div>
              <p className="text-[13px] text-ink-secondary mb-6">
                Track your submission status anytime on your{" "}
                <Link to="/dashboard" className="underline text-accent">dashboard</Link>.
              </p>
              {pdfUrl ? (
                <a
                  href={pdfUrl}
                  download={pdfName}
                  className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full border border-line bg-accent px-5 text-[14.5px] font-semibold text-ink hover:opacity-90"
                >
                  <Download className="h-4 w-4" />
                  Download your one-pager (PDF)
                </a>
              ) : (
                <button
                  type="button"
                  disabled
                  className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full border border-line px-5 text-[14.5px] font-semibold text-ink-secondary disabled:opacity-60"
                >
                  <Download className="h-4 w-4" />
                  Preparing…
                </button>
              )}
            </>
          )}

          {(state === "unpaid" || state === "error") && (
            <>
              <p className="font-script text-accent text-[22px] leading-none">one moment</p>
              <h1 className="font-display text-[22px] font-semibold mt-2 mb-2">
                We couldn't confirm the payment yet
              </h1>
              <p className="text-[14px] text-ink-secondary">
                If you were charged, refresh in a moment or email us at{" "}
                <a href="mailto:hello@deckscore.ai" className="underline text-ink">
                  hello@deckscore.ai
                </a>
                .
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
